class AdvancedModal extends HTMLElement {
  constructor () {
    super();
    this.selectors$g = {
      open: '[data-popup-open]',
      close: '[data-popup-close]',
    };
    this.attributes$c = {
      closing: 'closing',
      ignoreWrapper: '[data-ignore-action-wrapper]',
    };
    this.type = this.getAttribute('data-type');
    this.ID = this.getAttribute('data-id') ?? this.id;
    this.popup = this.querySelector('dialog');
    this.section = this.closest('.shopify-section');
    if (!this.popup && this.section) {
      this.popup = this.section.querySelector(`*[data-target="${this.ID}"]`);
    }
    this.isAnimating = false;
    this.showModal = true;
    this.closeOnOverlay = this.getAttribute('data-close-on-overlay') !== 'false';
    this.closeOnEsc = this.getAttribute('data-close-on-esc') !== 'false';
    this.enableScrollLock = this.hasAttribute('data-scroll-lock')
      ? true
      : this.type !== 'cart-item';
    const preventCloseAttr = this.getAttribute('data-prevent-close-while-nested-open');
    this.preventCloseWhileNestedOpen = preventCloseAttr === 'true';
    this.buttonPopupOpen = this.querySelector(this.selectors$g.open);
    this.swiperElement = this.buttonPopupOpen?.closest('swiper-container');

  }

  popupOpen () {
    if (!this.popup) return;
    this._stopSwiper();
    // Ensure reopening can't inherit stale closing state.
    this.popup.removeAttribute(this.attributes$c.closing);
    this.popup.setAttribute('open', '');
    this.popup.removeAttribute('inert');
    this.popup.setAttribute('aria-hidden', 'false');
    if (this.enableScrollLock) {
      this.popup.setAttribute('data-scroll-lock-required', '');
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    }
  }

  hasOpenModals () {
    const hasOpenDialogs = Boolean(document.querySelectorAll('dialog[open][data-scroll-lock-required]').length);
    if (hasOpenDialogs) {
      return true;
    }

    const hasDrawerOpenClass = document.body?.classList.contains('js-drawer-open')
      || document.documentElement?.classList.contains('js-drawer-open');
    if (hasDrawerOpenClass) {
      return true;
    }

    return Boolean(document.querySelector('.drawer.is-open, .drawer.drawer--is-open, .drawer--is-open'));
  }

  popupClose () {
    if (!this.popup || this.popup.hasAttribute('inert')) {
      return;
    }

    if (this.preventCloseWhileNestedOpen) {
      const hasOpenNestedDialog = Array.from(this.popup.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.popup);
      if (hasOpenNestedDialog) {
        return;
      }
    }

    this._startSwiper();
    this.popup.removeAttribute('open');
    this.popupCloseActions();
  }

  popupCloseActions () {
    // Always clear the transitional state, even when another close handler has
    // already made the dialog inert. A stale closing attribute keeps the
    // backdrop visible by design.
    this.popup.removeAttribute(this.attributes$c.closing);
    if (this.popup.hasAttribute('inert')) return;
    this.popup.setAttribute('inert', '');
    this.popup.removeAttribute('data-scroll-lock-required');
    this.popup.setAttribute('aria-hidden', 'false');
    if (!this.hasOpenModals() && this.enableScrollLock) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
    this.popup.dispatchEvent(new CustomEvent('theme:popup:onclose', { bubbles: false }));
  }

  _startSwiper () {
    this._setSwiperStatus('start');
  }

  _stopSwiper () {
    this._setSwiperStatus('stop');
  }

  _setSwiperStatus (action) {
    if (this.swiperElement && this.swiperElement.hasAttribute('autoplay')) {
      const swiper = this.swiperElement.hasOwnProperty('swiper') ? this.swiperElement.swiper : null;
      if (swiper.hasOwnProperty('autoplay')) {
        switch (action) {
          case 'start':
            swiper.autoplay.start();
            break;
          case 'stop':
            swiper.autoplay.stop();
            break;
        }
      }
    }
  }

  popupEvents () {
    if (!this.popup) return;
    const hasNestedDialogInPath = (event) => {
      if (typeof event.composedPath !== 'function') {
        return false;
      }

      return event.composedPath().some((node) => {
        return node && node.nodeName === 'DIALOG' && node !== this.popup;
      });
    };

    // Open button click event
    this.buttonPopupOpen?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.popupOpen();
    });

    const closeButtons = Array.from(this.popup.querySelectorAll(this.selectors$g.close)).filter((button) => {
      // Only bind close handlers for buttons owned by this dialog.
      // Without this guard, nested dialogs can close their parent modal too.
      const ownerDialog = button.closest('dialog');
      if (ownerDialog && ownerDialog !== this.popup) {
        return false;
      }

      const ignoredWrapper = button.closest(this.attributes$c.ignoreWrapper);
      return !ignoredWrapper || !this.popup.contains(ignoredWrapper);
    });

    if (closeButtons.length) {
      closeButtons.forEach((closeButton) => {
        closeButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.popupClose();
        });
      });
    }

    // Close dialog on click outside content
    if (this.showModal && this.closeOnOverlay) {
      this.popup.addEventListener('click', (event) => {
        if (hasNestedDialogInPath(event)) {
          return;
        }

        if (event.target === this.popup && event.type === 'click') {
          this.popupClose();
        }
      });
    }

    // Close dialog on click ESC key pressed
    this.popup.addEventListener('keydown', (event) => {
      if (hasNestedDialogInPath(event)) {
        return;
      }

      const ownerDialog = event.target instanceof Element ? event.target.closest('dialog') : null;
      if (ownerDialog && ownerDialog !== this.popup) {
        return;
      }

      if (event.code === 'Escape' && this.closeOnEsc) {
        event.preventDefault();
        this.popupClose();
      }
    });

    this.popup.addEventListener('close', () => this.popupCloseActions());
  }

  connectedCallback () {
    this.popupEvents();
  }
}

customElements.define('advanced-modal', AdvancedModal);

if (!customElements.get('product-modal')) {
  class ProductModal extends AdvancedModal {
    constructor () {
      super();
      this.url = this.buttonPopupOpen?.href ?? this.buttonPopupOpen?.dataset?.href;
      this.variantsCount = this.buttonPopupOpen?.dataset?.variantsCount ?? null;

      this.disabled = this.buttonPopupOpen.hasAttribute('disabled');
      this.productElements = {};
      this.modalContent = this.popup?.querySelector('.advanced-modal__content-wrapper');
      this.boundCartAdded = this.cartAdded.bind(this);
      this.modalEventsBound = false;
      this.cartEventsBound = false;

      if (this.variantsCount) {
        this.variantsCount = this.variantsCount * 1;
      }
    }

    action () {
      if (this.variantsCount === 1) {
        const form = this.modalContent.querySelector('form[action*="cart/add"]');
        if (form) {
          form.dispatchEvent(new Event('submit'));
        } else {
          this.clearSingleVariantLoading();
          super.popupOpen();
        }
      } else {
        super.popupOpen();
      }
    }

    setSingleVariantLoading () {
      if (this.variantsCount !== 1) return;
      this.buttonPopupOpen.setAttribute('data-loading', '');
      document.querySelector('sticky-header')?.classList.add('loading-bar');
    }

    clearSingleVariantLoading () {
      if (this.variantsCount !== 1) return;
      this.buttonPopupOpen.removeAttribute('data-loading');
      document.querySelector('sticky-header')?.classList.remove('loading-bar');
    }

    popupOpen () {
      document.querySelectorAll('dialog[open]').forEach((item) => {
        const target = item.getAttribute('data-target');
        const ignoreTarget = item.hasAttribute('data-target-ignore');
        if (target && !ignoreTarget) {
          const modal = document.querySelector(`*[data-id="${target}"]`);
          if (modal && target !== this.getAttribute('data-id')) {
            modal.popupClose();
          }
        }
      });
      this.url = this.buttonPopupOpen?.href ?? this.buttonPopupOpen?.dataset?.href;
      if (!this.popup || !this.url || this.disabled) return;

      if (this.productElements[this.url]) {
        this.setInnerHTML(this.modalContent, this.productElements[this.url]);
        this.setSingleVariantLoading();
        this.action();
        return;
      }
      this.buttonPopupOpen.setAttribute('data-loading', '');
      if (this.variantsCount === 1) {
        document.querySelector('sticky-header')?.classList.add('loading-bar');
      }
      fetch(this.url).then((response) => response.text()).then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productTemplate = responseHTML.querySelector('product-template');
        if (productTemplate) {
          productTemplate.id = productTemplate.id.replace('ProductSection', 'QuickModalSection');
          productTemplate.removeAttribute('data-enable-history-state');
          productTemplate.setAttribute('disabled', 'true');
          let element = productTemplate.closest('.shopify-section');
          element = this.removeDOMElements(element);
          this.productElements[this.url] = element.innerHTML;
          this.setInnerHTML(this.modalContent, this.productElements[this.url]);
          this.action();
        }
      }).finally(() => {
        if (this.variantsCount !== 1) {
          this.buttonPopupOpen.removeAttribute('data-loading');
          ///document.querySelector('sticky-header').classList.remove('loading-bar');
        }
      });
    }

    removeDOMElements (element) {
      element.querySelectorAll('*[loading="lazy"]').forEach((item) => {
        item.removeAttribute('loading');
      });
      element.querySelectorAll('.product-single__sticky').forEach((item) => {
        item.classList.remove('product-single__sticky');
      });
      element.querySelectorAll('.advanced-product-size-guide').forEach((item) => item.remove());
      element.querySelector('#sr-getItByTimer-holder')?.remove();
      element.querySelector('.advanced-accordions')?.remove();
      return element;
    }

    cartAdded () {
      super._startSwiper();
      this.clearSingleVariantLoading();
    }

    setInnerHTML (element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
      element.querySelector('product-template')?.setAttribute('disabled', 'false');
      document.dispatchEvent(new CustomEvent('theme:content:loaded', {
        detail: { container: element },
      }));

    }

    connectedCallback () {
      if (!this.modalEventsBound) {
        super.connectedCallback();
        this.modalEventsBound = true;
      }
      if (this.variantsCount === 1 && !this.cartEventsBound) {
        document.addEventListener('ajaxProduct:added', this.boundCartAdded);
        document.addEventListener('ajaxProduct:error', this.boundCartAdded);
        this.cartEventsBound = true;

      }
    }

    disconnectedCallback () {
      this.productElements = {};
      if (this.modalContent) {
        this.modalContent.innerHTML = '';
      }
      if (this.variantsCount === 1) {
        document.removeEventListener('ajaxProduct:added', this.boundCartAdded);
        document.removeEventListener('ajaxProduct:error', this.boundCartAdded);
        this.cartEventsBound = false;
      }
    }
  }

  customElements.define('product-modal', ProductModal);
}

class AdvancedSizeGuideDrawer extends AdvancedModal {
  constructor () {
    super();
    this.enableScrollLock = this.hasAttribute('data-scroll-lock') ? true : window.innerWidth <= 749;
    this.url = this.buttonPopupOpen.getAttribute('href');
    this.content = this.querySelector('[data-content]');
    this.loadingMarkup = this.content ? this.content.innerHTML : '';
    this.timeOut = window?.Shopify.designMode === true ? 500 : 0;
    this.switcher = this.querySelector('[data-size-guide-switcher]');
    this.requestId = 0;
  }

  async connectedCallback () {
    super.connectedCallback();
    this.initSwitcher();
    if (this.url) {
      setTimeout(() => this.loadContent(this.url), this.timeOut);
    }
  }

  // Shared across drawer instances: the same guide is often rendered twice (desktop/mobile selectors).
  static fetchPage (url) {
    AdvancedSizeGuideDrawer.pageCache ??= new Map();
    if (!AdvancedSizeGuideDrawer.pageCache.has(url)) {
      const request = fetch(url)
        .then((response) => response.text())
        .catch((error) => {
          AdvancedSizeGuideDrawer.pageCache.delete(url);
          throw error;
        });
      AdvancedSizeGuideDrawer.pageCache.set(url, request);
    }
    return AdvancedSizeGuideDrawer.pageCache.get(url);
  }

  initSwitcher () {
    if (!this.switcher || this.switcherBound) return;
    this.switcherBound = true;
    this.switcherToggle = this.switcher.querySelector('[data-size-guide-toggle]');
    this.switcherCurrent = this.switcher.querySelector('[data-size-guide-current]');
    this.switcherList = this.switcher.querySelector('[data-size-guide-options]');
    this.switcherOptions = [...this.switcher.querySelectorAll('[data-size-guide-option]')];

    this.switcherToggle.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleSwitcher();
    });

    this.switcherToggle.addEventListener('keydown', (event) => {
      if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
        event.preventDefault();
        this.toggleSwitcher(true);
      }
    });

    this.switcherOptions.forEach((option) => {
      option.addEventListener('click', () => this.selectSizeGuide(option));
    });

    this.switcherList.addEventListener('keydown', (event) => {
      const index = this.switcherOptions.indexOf(document.activeElement);
      switch (event.code) {
        case 'ArrowDown':
          event.preventDefault();
          this.switcherOptions[Math.min(index + 1, this.switcherOptions.length - 1)].focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.switcherOptions[Math.max(index - 1, 0)].focus();
          break;
        case 'Enter':
        case 'Space':
          event.preventDefault();
          if (index > -1) this.selectSizeGuide(this.switcherOptions[index]);
          break;
        case 'Tab':
          this.toggleSwitcher(false);
          break;
      }
    });

    // Escape closes the dropdown first; the drawer only closes on a second press.
    this.switcher.addEventListener('keydown', (event) => {
      if (event.code === 'Escape' && !this.switcherList.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleSwitcher(false);
        this.switcherToggle.focus();
      }
    });

    this.popup?.addEventListener('click', (event) => {
      if (!this.switcherList.hidden && !this.switcher.contains(event.target)) {
        this.toggleSwitcher(false);
      }
    });
    this.popup?.addEventListener('theme:popup:onclose', () => this.toggleSwitcher(false));
  }

  toggleSwitcher (force) {
    if (!this.switcherList) return;
    const open = typeof force === 'boolean' ? force : this.switcherList.hidden;
    this.switcherList.hidden = !open;
    this.switcherToggle.setAttribute('aria-expanded', String(open));
    if (open) {
      const selected = this.switcherOptions.find((option) => option.getAttribute('aria-selected') === 'true');
      (selected || this.switcherOptions[0])?.focus();
    }
  }

  selectSizeGuide (option) {
    const url = option.getAttribute('data-url');
    this.toggleSwitcher(false);
    this.switcherToggle.focus();
    if (!url || url === this.url) return;

    this.url = url;
    this.buttonPopupOpen.setAttribute('href', url);
    this.switcherOptions.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
    if (this.switcherCurrent) {
      this.switcherCurrent.textContent = option.textContent.trim();
    }
    this.content.innerHTML = this.loadingMarkup;
    this.loadContent(url);
  }

  async loadContent (url) {
    const requestId = ++this.requestId;
    this.content.setAttribute('aria-busy', 'true');
    let text;
    try {
      text = await AdvancedSizeGuideDrawer.fetchPage(url);
    } catch (error) {
      console.error(error);
      if (requestId === this.requestId) this.content.removeAttribute('aria-busy');
      return;
    }
    // A newer guide was selected while this one was loading.
    if (requestId !== this.requestId) return;
    this.content.removeAttribute('aria-busy');

    const html = document.createElement('div');
    html.innerHTML = text;
    const description = html.querySelector('.advanced-page-description');
    const navigation = html.querySelector('.advanced-page-navigation');
    if (description) {
      description.remove();
    }
    if (navigation) {
      navigation.remove();
    }
    // The switcher header replaces the page's own title.
    if (this.switcher) {
      html.querySelectorAll('.advanced-page-heading-container').forEach(el => el.remove());
    }
    let mainContent = html.querySelector('#MainContent');
    if (mainContent && mainContent.querySelector('.icartShopifyCartContent')) {
      mainContent = mainContent.querySelector('.icartShopifyCartContent');
    }

    if (mainContent) {
      const swipers = mainContent.querySelectorAll('swiper-container');
      swipers.forEach(swiper => {
        swiper.removeAttribute('breakpoints');
      });

      const patternElements = mainContent.querySelectorAll('[class*="section-template--"]');
      patternElements.forEach(el => {
        const classesToRemove = [...el.classList].filter(className =>
          /^section-template--\d+__advanced_page_heading_[a-zA-Z0-9]+-padding$/.test(className) ||
          /^section-template--\d+__main-media-padding$/.test(className),
        );
        classesToRemove.forEach(cls => el.classList.remove(cls));
      });

      const pageWidthElements = mainContent.querySelectorAll('.page-width');
      pageWidthElements.forEach(el => el.classList.remove('page-width'));

      mainContent.querySelectorAll('.size-guide-media-rich-text__content.columns').forEach((item) => {
        item.classList.remove('columns');
      });
      [...mainContent.querySelectorAll('meta')].forEach(el => el.remove());

      mainContent.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
        const href = link.getAttribute('href');
        if (href && !document.head.querySelector(`link[href="${href}"]`)) {
          const clonedLink = link.cloneNode(true);
          document.head.appendChild(clonedLink);
        }
        link.remove();
      });

      const sections = mainContent.querySelectorAll('section');
      sections.forEach(section => {
        const div = document.createElement('div');
        [...section.attributes].forEach(attr => {
          div.setAttribute(attr.name, attr.value);
        });
        while (section.firstChild) {
          div.appendChild(section.firstChild);
        }
        section.parentNode.replaceChild(div, section);
      });

      const clone = mainContent.cloneNode(true);
      this.content.replaceChildren(...clone.childNodes);

      //     this.content.innerHTML = mainContent.innerHTML;
    }
  }

  popupClose () {
    if (!this.popup || this.popup.hasAttribute('inert')) {
      return;
    }
    this.popup.removeAttribute('open');
    this.popup.setAttribute(this.attributes$c.closing, '');
    setTimeout(() => {
      this.popupCloseActions();
    }, 300);
  }
}

customElements.define('advanced-size-guide-drawer', AdvancedSizeGuideDrawer);

function debounce (fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements (container) {
  return Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^=\'-\']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe',
    ),
  );
}

const trapFocusHandlers = {};

function trapFocus (container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus (elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape (event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class SearchForm extends HTMLElement {

  constructor () {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }
  }

  toggleResetButton () {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange () {
    this.toggleResetButton();
  }

  shouldResetForm () {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset (event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

class PredictiveSearch extends SearchForm {
  constructor () {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners () {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery () {
    return this.input.value.trim();
  }

  onChange () {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit (event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset (event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus () {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut () {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup (event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown (event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm (previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption (direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption () {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults (searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
        signal: this.abortController.signal,
      }).then((response) => {
      if (!response.ok) {
        const error = new Error(response.status);
        this.close();
        throw error;
      }

      return response.text();
    }).then((text) => {
      const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').
        querySelector('#shopify-section-predictive-search').innerHTML;
      // Save bandwidth keeping the cache in all instances synced
      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup);
    }).catch((error) => {
      if (error?.code === 20) {
        // Code 20 means the call was aborted
        return;
      }
      this.close();
      throw error;
    });
  }

  setLiveRegionLoadingState () {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText (statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults (resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults () {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight () {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header-advanced')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open () {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close (clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults (clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

// SG 2026-10-21: the drawer's height vars (--viewport-height, --header-bottom-position) are only read inside the
// header (menu drawer + menu icon). Set them there, and only when the value changes: a custom property on <html> is
// inherited by every element, so each write restyled the whole page. That ran on every menu open/close and on every
// resize while the menu is open, which Chrome on phones fires as its toolbars move, so the menu stalled there.
function sgSetHeaderVar (scope, name, value) {
  const el = (scope && scope.closest && scope.closest('.section-header-advanced')) || document.documentElement;
  if (el.style.getPropertyValue(name) !== value) el.style.setProperty(name, value);
}

class MenuDrawer extends HTMLElement {
  constructor () {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents () {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.boost-pfs-search-btn-clear-suggestion)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp (event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;
    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick (event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus () {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        sgSetHeaderVar(this, '--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer (summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer (event, elementToFocus = false) {
    if (event === undefined) return;
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', false);
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      if (!details.hasAttribute('data-forced-open')) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      }
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut () {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick (event) {
    if (!event.target.closest('show-more-button')) {
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu (detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation (detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor () {
    super();
    // SG 2026-10-21: the phone menu froze on its first open while the browser styled and laid out the drawer for the
    // first time. Do that once while the page is idle (open + close in one task, so nothing is painted), and store
    // the header position the drawer height needs, so the first tap only has the work a second tap has.
    const sgPrewarm = () => {
      const details = this.mainDetailsToggle;
      if (!details || details.hasAttribute('open')) return;
      this.header = this.header || document.querySelector('.section-header-advanced');
      if (!this.header) return;
      const wrapper = this.closest('.header-wrapper');
      this.borderOffset = wrapper && wrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
      this.header.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
      this.header.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      details.setAttribute('open', '');
      void details.offsetHeight;
      details.removeAttribute('open');
    };
    const sgSchedule = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(sgPrewarm, { timeout: 4000 });
      else setTimeout(sgPrewarm, 2500);
    };
    if (document.readyState === 'complete') sgSchedule();
    else window.addEventListener('load', sgSchedule, { once: true });
  }

  openMenuDrawer (summaryElement) {
    this.header = this.header || document.querySelector('.section-header-advanced');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    // SG 2026-10-21: set on the header, not <html>. A custom property on <html> is inherited by every element, so the
    // whole page (7k+ nodes) restyled on each open and the phone menu stalled; only the drawer and the menu icon
    // (inside the header) read it. Skipped when the value hasn't changed.
    const sgHeaderBottom = `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`;
    if (this.header.style.getPropertyValue('--header-bottom-position') !== sgHeaderBottom) {
      this.header.style.setProperty('--header-bottom-position', sgHeaderBottom);
    }
    this.header.classList.add('menu-open');
    document.querySelector('.header-wrapper').dispatchEvent(new CustomEvent('menu-open', {
      bubbles: true,
      detail: { id: 'drawer' },
    }));
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer (event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    document.querySelector('.header-wrapper').dispatchEvent(new CustomEvent('menu-close', {
      bubbles: true,
      detail: { id: 'drawer' },
    }));
    const form = this.querySelector('.menu-drawer-search-form');
    if (form) {
      form.style.display = 'none';
      form.querySelector('input[name="q"]').value = '';
      form.querySelector('input[name="q"]').dispatchEvent(new Event('change'));
    }
    const searchOpener = this.header.querySelector('search-opener');
    if (searchOpener) {
      searchOpener.style.visibility = 'visible';
    }
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    // once per frame: phone browsers fire several resizes while their toolbars slide
    if (this.sgResizeFrame) return;
    this.sgResizeFrame = requestAnimationFrame(() => {
      this.sgResizeFrame = null;
      this.header && sgSetHeaderVar(
        this.header,
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
      sgSetHeaderVar(this, '--viewport-height', `${window.innerHeight}px`);
    });
  };
}

customElements.define('header-drawer', HeaderDrawer);

class DetailsDisclosure extends HTMLElement {
  constructor () {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut () {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle () {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close () {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends HTMLElement {
  constructor () {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  connectedCallback () {
    if (!this.isTouchDevice) {
      this.querySelectorAll('.header__menu-item').forEach((item) => {
        item.addEventListener('mouseover', (e) => {
          this.header.querySelectorAll('details').forEach((item) => {
            item.removeAttribute('open');
          });

          if (e.target.closest('details')) {
            e.target.closest('details').setAttribute('open', true);
          }
        });
      });
      this.header.addEventListener('mouseleave', () => {
        this.header.querySelectorAll('.header__inline-menu details').forEach((item) => {
          item.removeAttribute('open');
        });
      });
      // SG 2026-10-23: hover opens the mega menu, then a mouse click on its label toggled it shut under the pointer.
      // Mouse clicks on an open menu keep it open; keyboard clicks (detail 0) still toggle as usual.
      this.querySelectorAll('details > summary.header__menu-item').forEach((summary) => {
        summary.addEventListener('click', (e) => {
          if (e.detail > 0 && summary.parentElement.hasAttribute('open')) e.preventDefault();
        });
      });
    }
    const details = this.header.querySelectorAll('.header__inline-menu details, .header__icons details');
    details.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (item.open) {
          this.header.dispatchEvent(new CustomEvent('menu-open', {
            bubbles: true,
            detail: { id: item.id },
          }));
        } else {
          this.header.dispatchEvent(new CustomEvent('menu-close', {
            bubbles: true,
            detail: { id: item.id },
          }));
        }
      });
      item.addEventListener('click', (e) => {
        setTimeout(() => {
          const root = e.target.closest('details');
          if (root) {
            if (this.isTouchDevice) {
              details.forEach((item) => {
                if (item.getAttribute('id') !== root.getAttribute('id')) {
                  item.removeAttribute('open');
                }
              });
            } else if (root.closest('.header__search')) {
              details.forEach((item) => {
                if (item.getAttribute('id') !== root.getAttribute('id')) {
                  item.removeAttribute('open');
                }
              });
            }
            this.header.preventHide = root.open;
            if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
            document.documentElement.style.setProperty(
              '--header-bottom-position-desktop',
              `${Math.floor(this.header.getBoundingClientRect().bottom)}px`,
            );
          }
        }, 10);
      });
    });
  }
}

customElements.define('header-menu', HeaderMenu);

class SearchOpener extends HTMLElement {
  constructor () {
    super();
    this.tagget = this.getAttribute('data-target');
    this.summary = this.querySelector('summary');
  }

  handleClick () {
    if (this.tagget) {
      const controller = document.querySelector(this.tagget);
      if (!controller) {
        return;
      }
      if (!controller.hasAttribute('open') || controller.getAttribute('open') === 'false') {
        const summary = controller.querySelector('summary');
        if (summary) {
          summary.dispatchEvent(new Event('click'));
        }
        controller.setAttribute('open', 'true');
      }
      const form = controller.querySelector('.menu-drawer-search-form');
      if (form) {
        form.style.display = 'block';
        this.style.visibility = 'hidden';
        setTimeout(() => {
          form.querySelector('input[name="q"]').focus();
        }, 500);
      }
    }
  }

  connectedCallback () {
    if (this.summary) {
      this.summary.addEventListener('click', this.handleClick.bind(this));
    }
  }

  disconnectedCallback () {
    if (this.summary) {
      this.summary.removeEventListener('click', this.handleClick.bind(this));
    }
  }
}

customElements.define('search-opener', SearchOpener);

document.addEventListener('DOMContentLoaded', function () {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (!isTouchDevice) return;
  document.querySelectorAll('swiper-container[autoplay-pause-on-mouse-enter="true"]').forEach(function (swiperEl) {
    const waitForSwiper = setInterval(() => {
      if (swiperEl.swiper && swiperEl.swiper.autoplay) {
        clearInterval(waitForSwiper);
        const swiperInstance = swiperEl.swiper;

        let isPausedByTouch = false;
        let isSliderVisible = false;

        const pauseAutoplay = () => {
          if (!isPausedByTouch) {
            isPausedByTouch = true;
            swiperInstance.autoplay.stop();
          }
        };

        const resumeAutoplay = () => {
          if (isPausedByTouch) {
            isPausedByTouch = false;
            swiperInstance.autoplay.start();
          }
        };

        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            isSliderVisible = entry.isIntersecting;
            if (isSliderVisible) {
              pauseAutoplay();
            } else {
              resumeAutoplay();
            }
          });
        }, { threshold: 1 });
        observer.observe(swiperEl);

        window.addEventListener('touchmove', function (e) {

          if (!e.touches || e.touches.length === 0) return;

          const touch = e.touches[0];
          const rect = swiperEl.getBoundingClientRect();
          const isInside = (
            touch.clientX >= rect.left &&
            touch.clientX <= rect.right &&
            touch.clientY >= rect.top &&
            touch.clientY <= rect.bottom
          );
          if (isInside) {
            pauseAutoplay();
          } else {
            resumeAutoplay();
          }
        }, { passive: true });

        window.addEventListener('touchend', () => {
          if (!isSliderVisible) {
            resumeAutoplay();
          }
        }, { passive: true });
        window.addEventListener('touchcancel',
          () => {
            if (!isSliderVisible) {
              resumeAutoplay();
            }
          }, { passive: true });
      }
    }, 100);
  });
});

/* SG 2026-10-21: scrollbar width for the scroll-lock padding (design-system.css, body.overflow-hidden-*). Measured while
   scrolling isn't locked; only written when it changes. */
(function () {
  function measure () {
    const body = document.body;
    if (!body || /(^|\s)overflow-hidden-/.test(body.className)) return;
    const width = `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`;
    if (body.style.getPropertyValue('--sg-scrollbar-w') !== width) body.style.setProperty('--sg-scrollbar-w', width);
  }
  let frame = null;
  function schedule () {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = null; measure(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', measure, { once: true });
  else measure();
  window.addEventListener('load', measure, { once: true });
  window.addEventListener('resize', schedule);
})();
